import { Button, Tooltip, message, Space, Input } from "antd";
import { PlusOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import CapMaterialTable from "components/inventory/CapMaterialTable";
import CapMaterialsTable from "components/inventory/CapMaterialsTable";
import CapTable from "components/inventory/CapTable";
import DottedStock from "components/inventory/DottedStock";
import FlexiStock from "components/inventory/FlexiStock";
import GrandleStock from "components/inventory/GrandleStock";
import MeshStock from "components/inventory/MeshStock";
import Popcorn from "components/inventory/Popcorn";
import TwillCotton from "components/inventory/TwillCotton";
import AddQuantityModal from "components/inventory/AddQuantityModal";
import InventoryActivityLogModal from "components/inventory/InventoryActivityLogModal";
import { inventoryData } from "features/jobinv/JobCardSlice";
import { useFetchCapsQuery, useFetchInventoryQuery, useUpdateInventoryMutation, useCreateInventoryMutation, useFetchInventoryActivityLogQuery, useFetchInventoryClothTypesQuery } from "features/store";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

const Inventory = () => {
  const { user } = useSelector((state) => state.user);
  const userRole = user?.roll || user?.role || (user?.user && (user.user.roll || user.user.role)) || (JSON.parse(localStorage.getItem("user") || "{}").roll);
  const isViewer = userRole === "viewer";

  // Fetch dynamic cloth types from backend (default to CLOTH MATERIAL)
  const { data: clothTypesResp, isLoading: clothTypesLoading } = useFetchInventoryClothTypesQuery({ type: "CLOTH MATERIAL" });
  const types = clothTypesResp?.cloth_types || [];

  // State to manage pagination for each table (cloth type)
  const [paginationState, setPaginationState] = useState({});

  // Initialize/refresh pagination state when cloth types load or change
  useEffect(() => {
    if (types && types.length) {
      setPaginationState((prev) => {
        const next = { ...prev };
        types.forEach((item) => {
          if (!next[item]) {
            next[item] = { current: 1, pageSize: 10 };
          }
        });
        // Remove pagination entries for cloth types no longer present
        Object.keys(next).forEach((key) => {
          if (!types.includes(key)) {
            delete next[key];
          }
        });
        return next;
      });
    }
  }, [types]);

  // State to manage modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [activityLogModal, setActivityLogModal] = useState({
    visible: false,
    inventoryId: null,
  });

  // State to manage global color search
  const [colorSearch, setColorSearch] = useState("");
  const [updateInventory, { isLoading: isUpdating }] = useUpdateInventoryMutation();
  const [createInventory, { isLoading: isCreating }] = useCreateInventoryMutation();

  // Fetch activity log data
  const {
    data: activityLogData,
    isLoading: isActivityLogLoading,
  } = useFetchInventoryActivityLogQuery(activityLogModal.inventoryId || 0, {
    skip: !activityLogModal.visible || !activityLogModal.inventoryId,
  });

  // Handler to update pagination for a specific table
  const handlePaginationChange = (clothType, page, pageSize) => {
    setPaginationState((prev) => ({
      ...prev,
      [clothType]: {
        current: page,
        pageSize: pageSize || prev[clothType].pageSize,
      },
    }));
  };

  // Handler to open modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Handler to close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handler to open update modal
  const handleOpenUpdateModal = () => {
    setIsUpdateModalOpen(true);
  };

  // Handler to close update modal
  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  // Handler for activity log click
  const handleActivityLogClick = (inventoryId) => {
    setActivityLogModal({ visible: true, inventoryId });
  };

  // Handler to close activity log modal
  const handleCloseActivityLogModal = () => {
    setActivityLogModal({ visible: false, inventoryId: null });
  };

  // Handler for modal OK button (Add)
  const handleModalOk = async (formData) => {
    try {
      // If inventory exists, update it; otherwise create new
      if (formData.id || formData.inventoryId) {
        await updateInventory(formData).unwrap();
        message.success('Quantity added successfully!');
      } else {
        const result = await createInventory(formData).unwrap();
        // Handle multiple items response
        if (result.items && Array.isArray(result.items)) {
          message.success(`Successfully created ${result.items.length} inventory item(s)!`);
        } else if (result.created_items && Array.isArray(result.created_items)) {
          message.success(`Successfully created ${result.created_items.length} inventory item(s)!`);
        } else {
          message.success('Inventory created successfully!');
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving inventory:', error);
      message.error(error?.data?.message || error?.data?.error || 'Failed to save inventory. Please try again.');
    }
  };

  // Handler for update modal OK button
  const handleUpdateModalOk = async (formData) => {
    try {
      // Update mode - must have inventory ID
      if (formData.id || formData.inventoryId) {
        await updateInventory(formData).unwrap();
        message.success('Inventory updated successfully!');
        setIsUpdateModalOpen(false);
      } else {
        message.error('Please select an inventory item to update');
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      message.error(error?.data?.message || error?.data?.error || 'Failed to update inventory. Please try again.');
    }
  };

  return (
    <Style>
      <div>
        <div className="add-button-container">
          {!isViewer && (
            <Space size="middle">
              <Tooltip title="add quantity">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={handleOpenModal}
                >
                  ADD
                </Button>
              </Tooltip>
              <Tooltip title="update quantity">
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  size="large"
                  onClick={handleOpenUpdateModal}
                >
                  UPDATE
                </Button>
              </Tooltip>
            </Space>
          )}
          <div className="search-container">
            <Input
              placeholder="Search by color..."
              prefix={<SearchOutlined />}
              value={colorSearch}
              onChange={(e) => setColorSearch(e.target.value)}
              allowClear
              style={{ width: 300 }}
            />
          </div>
        </div>
        <div className="inventory-tables">
          {clothTypesLoading && (
            <div style={{ margin: "0 5rem" }}>Loading cloth types…</div>
          )}
          {!clothTypesLoading && types.length === 0 && (
            <div style={{ margin: "0 5rem" }}>No cloth types found.</div>
          )}
          {!clothTypesLoading && types.map((item) => (
            <FlexiStock
              key={item}
              item={item}
              pagination={paginationState[item]}
              onPaginationChange={(page, pageSize) =>
                handlePaginationChange(item, page, pageSize)
              }
              onActivityLogClick={handleActivityLogClick}
              colorSearch={colorSearch}
            />
          ))}
        </div>
        <div className="cap-material">
          {/* <CapMaterialsTable /> */}
        </div>
      </div>
      <AddQuantityModal
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={handleModalOk}
        loading={isUpdating || isCreating}
      />
      <AddQuantityModal
        open={isUpdateModalOpen}
        onCancel={handleCloseUpdateModal}
        onOk={handleUpdateModalOk}
        loading={isUpdating}
        isUpdateMode={true}
      />
      <InventoryActivityLogModal
        open={activityLogModal.visible}
        onCancel={handleCloseActivityLogModal}
        inventoryId={activityLogModal.inventoryId}
        activityData={activityLogData}
        loading={isActivityLogLoading}
      />
    </Style>
  );
};

export default Inventory;

const Style = styled.div`
.add-button-container {
  margin: 2rem 5rem 1rem 5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-container {
  display: flex;
  align-items: center;
}

.cap-material{
  margin: 2rem 5rem;
}
`;
